import classNames from 'classnames'
import classnames from 'classnames'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import { SliderThemes } from '~/components/atoms/Slider/sliderThemes'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ComponentTheme } from '~/components/themes'

import styles from './QuoteSlider.module.css'

export interface QuoteSliderProps {
  testID?: string
  items: {
    quote: PortableTextComponentProps['blocks']
    image?: ImageProps
    authorText: string
    imageType?: 'author' | 'top'
  }[]
  moduleId?: string
  keyLineTop?: boolean
  keyLineBottom?: boolean
  theme?: ComponentTheme
  negativeMarginTop?: boolean
  largePaddingBottom?: boolean
  fullScreen?: boolean
}

export default function QuoteSlider({
  testID,
  items,
  moduleId,
  keyLineTop = false,
  keyLineBottom = false,
  theme,
  negativeMarginTop = true,
  largePaddingBottom = false,
  fullScreen = false,
}: QuoteSliderProps) {
  const renderSlider = () => {
    return (
      <Fixture
        className={classNames(
          styles.base,
          keyLineTop && styles.keyLineTop,
          keyLineBottom && styles.keyLineBottom,
          largePaddingBottom && styles.largePaddingBottom
        )}
        data-testid={testID}
        id={moduleId}
        verticalPadding={true}
        noPaddingBottom={true}
        theme={theme}
      >
        <Container cssClass={classnames(styles.sliderContainer, styles.theme)}>
          <DynamicSlider
            title=""
            slidesPerView={1}
            slidesPerViewMed={1}
            slidesPerViewLrg={1}
            overflowVisible={false}
            hideTitle={true}
            hideArrows={false}
            hideArrowsMobile={true}
            theme={SliderThemes['light']}
            useScrollbar
            spacer={false}
            extendRight={true}
            wrapperClass={styles.sliderWrapperClass}
            customControls={styles.sliderControls}
          >
            {items.map(({ image, quote, authorText, imageType }, index) => (
              <div
                className={styles.contentContainer}
                key={`quote-item-${index}`}
              >
                <div className={styles.content}>
                  {image?.asset && imageType === 'top' && (
                    <div className={classnames(styles.topImage)}>
                      {image?.asset && (
                        <Image asset={image.asset} objectFit="contain" />
                      )}
                    </div>
                  )}
                  <div className={styles.quote}>
                    <BlockContent
                      value={quote}
                      size={TextSizes.magPrimaryTitleMed}
                    />
                  </div>

                  <div className={styles.teamBio}>
                    <div className={styles.teamBioContentWrapper}>
                      {image?.asset && imageType === 'author' && (
                        <div className={classnames(styles.avatar)}>
                          {image?.asset && (
                            <Image asset={image.asset} width={60} />
                          )}
                        </div>
                      )}
                      <div className={styles.teamBioContent}>
                        <Text size={TextSizes.primaryTitleSml}>
                          <span
                            dangerouslySetInnerHTML={{ __html: authorText }}
                          />
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </DynamicSlider>
        </Container>
      </Fixture>
    )
  }

  if (negativeMarginTop) {
    return (
      <div
        className={classNames(
          fullScreen && styles.fullScreen,
          theme && styles[`fullScreen${theme}`]
        )}
      >
        <Container>
          <div className={styles.negativeMarginTop}>{renderSlider()}</div>
        </Container>
      </div>
    )
  }

  return renderSlider()
}
